import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';

export function useApi<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!path);
  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await api<T>(path));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => {
    void load();
  }, [load]);
  return { data, error, loading, reload: load };
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-brand-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function Card({ title, children, actions, className = '' }: { title?: ReactNode; children: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <section className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
      {(title || actions) && (
        <div className="mb-3 flex items-center justify-between gap-2">
          {title && <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

const TONES: Record<string, string> = {
  green: 'bg-emerald-100 text-emerald-900 ring-emerald-300',
  blue: 'bg-brand-100 text-brand-900 ring-brand-500/30',
  amber: 'bg-amber-100 text-amber-900 ring-amber-300',
  red: 'bg-red-100 text-red-900 ring-red-300',
  slate: 'bg-slate-100 text-slate-800 ring-slate-300',
  violet: 'bg-violet-100 text-violet-900 ring-violet-300',
};

export function Badge({ tone = 'slate', children, title }: { tone?: keyof typeof TONES; children: ReactNode; title?: string }) {
  return (
    <span title={title} className={`inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONES[tone]}`}>
      {children}
    </span>
  );
}

const STATE_LABEL: Record<string, [string, keyof typeof TONES]> = {
  REQUESTED: ['Solicitado', 'slate'],
  BRIEF_READY: ['Briefing pronto', 'slate'],
  PRIORITIZED: ['Priorizado', 'slate'],
  RESEARCHING: ['Pesquisando', 'blue'],
  DRAFTING: ['Redigindo', 'blue'],
  DESIGNING: ['Design', 'blue'],
  TECH_REVIEW: ['Revisão técnica', 'blue'],
  BRAND_COMPLIANCE_REVIEW: ['Compliance', 'blue'],
  PENDING_HUMAN_APPROVAL: ['Aguardando aprovação', 'amber'],
  APPROVED: ['Aprovado', 'green'],
  SCHEDULED: ['Agendado', 'green'],
  PUBLISHING: ['Publicando', 'green'],
  PUBLISHED: ['Publicado', 'green'],
  MEASURED: ['Medido', 'green'],
  ARCHIVED: ['Arquivado', 'slate'],
  NEEDS_INFO: ['Faltam informações', 'amber'],
  CHANGES_REQUESTED: ['Ajuste solicitado', 'amber'],
  BLOCKED: ['Bloqueado', 'red'],
  REJECTED: ['Rejeitado', 'red'],
  CANCELED: ['Cancelado', 'slate'],
  PUBLISH_FAILED: ['Falha de publicação', 'red'],
  AWAITING_MANUAL: ['Aguardando publicação manual', 'amber'],
  PENDING: ['Pendente', 'slate'],
  SUCCEEDED: ['Concluída', 'green'],
  PAUSED: ['Pausada', 'amber'],
  DEAD: ['Esgotou tentativas', 'red'],
  EXPIRED: ['Expirado', 'red'],
  IN_PROGRESS: ['Em andamento', 'blue'],
  DONE: ['Concluído', 'green'],
  ACTIVE: ['Ativo', 'green'],
  PLANNED: ['Planejado (inativo)', 'slate'],
  DISABLED: ['Desativado', 'red'],
  NOT_CONFIGURED: ['Não configurada', 'slate'],
  SIMULATED: ['Simulada', 'violet'],
  CONNECTED: ['Conectada', 'green'],
  ERROR: ['Erro', 'red'],
  IMPLEMENTED: ['Implementado', 'green'],
  PARTIAL: ['Parcial', 'amber'],
  NOT_IMPLEMENTED: ['Não implementado', 'slate'],
  PROPOSED: ['Proposto', 'amber'],
  CONFIRMED: ['Confirmado', 'green'],
  APROVADA: ['Aprovada', 'green'],
  CONDICIONAL: ['Condicional', 'amber'],
  NAO_HABILITADA: ['Não habilitada', 'red'],
  A_VALIDAR: ['A validar', 'amber'],
};

export function StateBadge({ state }: { state: string }) {
  const [label, tone] = STATE_LABEL[state] ?? [state, 'slate'];
  return (
    <Badge tone={tone} title={state}>
      {label}
    </Badge>
  );
}

export function RiskBadge({ risk }: { risk: string }) {
  const tone = ({ R0: 'slate', R1: 'blue', R2: 'amber', R3: 'red', R4: 'red' } as const)[risk as 'R0'] ?? 'slate';
  return <Badge tone={tone}>Risco {risk}</Badge>;
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  type = 'button',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
}) {
  const cls = {
    primary: 'bg-brand-700 text-white hover:bg-brand-800',
    secondary: 'bg-white text-brand-900 ring-1 ring-slate-300 hover:bg-slate-50',
    danger: 'bg-red-700 text-white hover:bg-red-800',
    success: 'bg-emerald-700 text-white hover:bg-emerald-800',
  }[variant];
  return (
    <button type={type} title={title} onClick={onClick} disabled={disabled} className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium shadow-sm disabled:cursor-not-allowed disabled:opacity-50 ${cls}`}>
      {children}
    </button>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-600">{children}</p>;
}

export function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div role="alert" className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
      {error}
    </div>
  );
}

export function Loading() {
  return <p className="text-sm text-slate-500" aria-live="polite">Carregando…</p>;
}

export function Field({ label, hint, children, required }: { label: string; hint?: string; children: ReactNode; required?: boolean }) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-slate-800">
        {label}
        {required && <span className="text-red-700"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

export const inputCls = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500';

export function Table({ head, children, empty }: { head: string[]; children: ReactNode; empty?: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
      {empty && <Empty>Nenhum registro.</Empty>}
    </div>
  );
}

export function Markdown({ text }: { text: string }) {
  // Renderização mínima e segura (sem HTML): títulos, listas e links.
  return (
    <div className="prose-md">
      {text.split('\n').map((line, i) => {
        if (line.startsWith('# ')) return <h1 key={i}>{line.slice(2)}</h1>;
        if (line.startsWith('## ')) return <h2 key={i}>{line.slice(3)}</h2>;
        const li = line.match(/^\s*- (.*)$/);
        const content = (li ? li[1] : line).split(/(\[[^\]]+\]\(https?:\/\/[^)]+\))/g).map((part, j) => {
          const m = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
          return m ? (
            <a key={j} href={m[2]} target="_blank" rel="noreferrer noopener" className="text-brand-700 underline">
              {m[1]}
            </a>
          ) : (
            <span key={j}>{part.replace(/\*\*/g, '')}</span>
          );
        });
        if (li) return <li key={i}>{content}</li>;
        return line.trim() ? <p key={i}>{content}</p> : <div key={i} className="h-2" />;
      })}
    </div>
  );
}

export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
      if (ok) setMessage(ok);
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}${e.details ? ` (${JSON.stringify(e.details).slice(0, 300)})` : ''}` : (e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, message, run };
}

export function Flash({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div role="status" className="mb-4 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
      {message}
    </div>
  );
}
